// The Supabase session as React state, telling a sign-out the user asked for from a session that
// ended by itself.

import { useEffect, useState } from "react";
import { navigate } from "./router.ts";
import { supabase } from "./supabase.ts";

export type SessionState =
  | { kind: "loading" }
  | { kind: "signedOut" }
  | { kind: "signedIn"; userId: string; email: string | null };

let signOutNotice: string | null = null;

/** Signs out, then shows Sign in with `notice` ("signed-out", "deleted", …). */
export async function signOut(notice: string): Promise<void> {
  signOutNotice = notice;
  await supabase.auth.signOut({ scope: "local" });
}

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ kind: "loading" });

  useEffect(() => {
    let wasSignedIn = false;
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        wasSignedIn = true;
        setState((current) =>
          current.kind === "signedIn" && current.userId === session.user.id
            ? current
            : { kind: "signedIn", userId: session.user.id, email: session.user.email ?? null });
        return;
      }
      if (event === "SIGNED_OUT" || event === "INITIAL_SESSION") {
        const notice = signOutNotice ?? (wasSignedIn ? "session-ended" : null);
        signOutNotice = null;
        wasSignedIn = false;
        setState({ kind: "signedOut" });
        // /dev-session is still setting its session; it navigates itself.
        if (window.location.pathname === "/dev-session") return;
        if (event === "SIGNED_OUT" || window.location.pathname !== "/sign-in") {
          navigate(notice ? `/sign-in?notice=${notice}` : "/sign-in", { replace: true });
        }
      }
    });
    return () => data.subscription.unsubscribe();
  }, []);

  return state;
}
